import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Search, UserRound, Users } from 'lucide-react'
import type { Role, ProjectRole } from '@one-deployment/shared'
import type { InviteList, InviteRequest, PeopleList, Person } from '@one-deployment/api'
import { Button, Card, Dialog, Input, Label, Menu, Select, SkeletonRows, Table, Td, Tr } from '@/components/ui'
import { Age, EmptyState, ErrorState } from '@/components/status'
import { RoleGate } from '@/components/RoleGate'
import { useProjects } from '@/features/shell/useOverview'
import { del, enc, get, patch, post, put } from '@/lib/api'
import { dateTime } from '@/lib/format'
import { qk } from '@/lib/query'
import { ask } from '@/components/ask'
import { cn } from '@/lib/cn'

const ROLES: Role[] = ['viewer', 'dev', 'lead', 'admin']
const PROJECT_ROLES: ProjectRole[] = ['viewer', 'dev', 'lead']

function InviteDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const qc = useQueryClient()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<Role>('dev')
  const m = useMutation({
    mutationFn: () => post('/api/people/invite', { email, globalRole: role, projects: [] } satisfies InviteRequest),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.invites() })
      setEmail('')
      onOpenChange(false)
    },
  })
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Invite a person" description="They get an email with a link valid for 7 days. Signup is invite-only.">
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); m.mutate() }}>
        <div><Label htmlFor="inv-email">Email</Label><Input id="inv-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus /></div>
        <div><Label htmlFor="inv-role">Global role</Label>
          <Select id="inv-role" value={role} onChange={(e) => setRole(e.target.value as Role)}>{ROLES.map((r) => <option key={r}>{r}</option>)}</Select>
        </div>
        {m.error && <ErrorState error={m.error} compact />}
        <div className="flex justify-end gap-2"><Button onClick={() => onOpenChange(false)}>Cancel</Button><Button type="submit" variant="primary" disabled={m.isPending}>Send invite</Button></div>
      </form>
    </Dialog>
  )
}

function AccessDialog({ person, onClose }: { person: Person; onClose: () => void }) {
  const qc = useQueryClient()
  const projects = useProjects().data?.projects ?? []
  const before: Record<string, ProjectRole> = Object.fromEntries(person.projects.map((p) => [p.key, p.role]))
  const [grants, setGrants] = useState<Record<string, ProjectRole | ''>>(() => before)
  const m = useMutation({
    // One grant per call, only for what changed: each is its own audited, idempotent write.
    mutationFn: async () => {
      for (const [key, role] of Object.entries(grants)) {
        if ((before[key] ?? '') === role) continue
        const path = `/api/projects/${enc(key)}/access/${enc(person.id)}`
        await (role ? put(path, { role }) : del(path))
      }
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: qk.people() }); onClose() },
  })
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={`Add ${person.name} to projects`} description="Non-admins see only the projects they are granted here. Admins have every right everywhere.">
      <div className="space-y-1">
        {projects.length === 0 && <p className="text-xs text-fg-subtle">No projects yet.</p>}
        {projects.map((p) => (
          <div key={p.key} className="flex h-8 items-center justify-between text-sm">
            <span>{p.name}</span>
            <Select value={grants[p.key] ?? ''} onChange={(e) => setGrants({ ...grants, [p.key]: e.target.value as ProjectRole | '' })}>
              <option value="">no access</option>
              {PROJECT_ROLES.map((r) => <option key={r}>{r}</option>)}
            </Select>
          </div>
        ))}
      </div>
      {m.error && <ErrorState error={m.error} compact />}
      <div className="mt-3 flex justify-end gap-2"><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={m.isPending} onClick={() => m.mutate()}>Save</Button></div>
    </Dialog>
  )
}

type Filter = 'all' | Role

const month = new Intl.DateTimeFormat('en', { month: 'short', year: 'numeric' })

/** Linear-style members list: search and role filter, then Active / Invited / Inactive sections. */
export function PeoplePage() {
  const qc = useQueryClient()
  const [inviting, setInviting] = useState(false)
  const [access, setAccess] = useState<Person | null>(null)
  const [roleFor, setRoleFor] = useState<Person | null>(null)
  const [q, setQ] = useState('')
  const [filter, setFilter] = useState<Filter>('all')
  const peopleQ = useQuery({ queryKey: qk.people(), queryFn: ({ signal }) => get<PeopleList>('/api/people', signal) })
  const invitesQ = useQuery({ queryKey: qk.invites(), queryFn: ({ signal }) => get<InviteList>('/api/invites', signal) })
  const projects = useProjects().data?.projects ?? []
  const projectName = (k: string) => projects.find((x) => x.key === k)?.name ?? k
  const act = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSettled: () => { void qc.invalidateQueries({ queryKey: qk.people() }); void qc.invalidateQueries({ queryKey: qk.invites() }) },
  })

  const needle = q.trim().toLowerCase()
  const match = (name: string, email: string, role: Role) => (filter === 'all' || role === filter) && (!needle || name.toLowerCase().includes(needle) || email.toLowerCase().includes(needle))
  const all = peopleQ.data?.people ?? []
  const active = all.filter((p) => !p.disabled && match(p.name, p.email, p.globalRole))
  const inactive = all.filter((p) => p.disabled && match(p.name, p.email, p.globalRole))
  const invites = (invitesQ.data?.invites ?? []).filter((i) => match(i.email, i.email, i.globalRole))

  const personRow = (p: Person) => (
    <Tr key={p.id} className={p.disabled ? 'opacity-50' : undefined}>
      <Td>
        <span className="flex items-center gap-3">
          {p.avatar ? <img src={p.avatar} alt="" className="size-7 shrink-0 rounded-full object-cover" /> : <span className="grid size-7 shrink-0 place-items-center rounded-full bg-surface-3 text-fg-subtle"><UserRound size={14} /></span>}
          <span className="min-w-0 leading-tight">
            <span className="block truncate font-medium">{p.name}</span>
            <span className="block truncate text-xs text-fg-faint">{p.email.split('@')[0]}</span>
          </span>
          {!p.mfaEnrolled && !p.disabled && <span className="shrink-0 rounded-full bg-warn/15 px-1.5 py-0.5 text-2xs text-warn" title="Has not enrolled two-factor sign-in yet">no 2FA</span>}
        </span>
      </Td>
      <Td className="text-fg-muted">{p.email}</Td>
      <Td><RoleBadge role={p.globalRole} muted={p.disabled} label={p.disabled ? 'Suspended' : undefined} /></Td>
      <Td className="text-fg-subtle">
        {p.globalRole === 'admin' ? <span title="Admins can open every project">All</span>
          : p.projects.length ? <span title={p.projects.map((g) => `${projectName(g.key)} (${g.role})`).join('\n')}>{p.projects.length} project{p.projects.length === 1 ? '' : 's'}</span>
          : <span className="text-fg-faint">None</span>}
      </Td>
      <Td className="text-fg-subtle">{p.createdAt ? month.format(p.createdAt) : '—'}</Td>
      <Td className="text-fg-subtle">{p.lastLoginAt ? <><Age from={p.lastLoginAt} /> ago</> : 'never'}</Td>
      <Td className="w-10">
        <RoleGate action="people.roles">
          <Menu trigger={<Button variant="ghost" size="sm" aria-label={`Actions for ${p.name}`}>⋯</Button>} items={[
            { label: 'Add to projects…', onSelect: () => setAccess(p) },
            { label: 'Change role…', onSelect: () => setRoleFor(p) },
            { label: 'Reset 2FA', onSelect: () => void ask({ title: `Reset 2FA for ${p.name}?`, body: 'They re-enrol at their next sign-in, and all their sessions end.', confirm: 'Reset 2FA', danger: true }).then((ok) => ok && act.mutate(() => post(`/api/people/${enc(p.id)}/reset-2fa`))) },
            'sep',
            p.disabled
              ? { label: 'Re-enable', onSelect: () => act.mutate(() => patch(`/api/people/${enc(p.id)}`, { disabled: false }, p.version)) }
              : { label: 'Suspend…', danger: true, onSelect: () => void ask({ title: `Suspend ${p.name}?`, body: 'They are signed out everywhere and cannot sign in until re-enabled.', confirm: 'Suspend', danger: true }).then((ok) => ok && act.mutate(() => patch(`/api/people/${enc(p.id)}`, { disabled: true }, p.version))) },
          ]} />
        </RoleGate>
      </Td>
    </Tr>
  )

  const section = (label: string, n: number) => (
    <tr><td colSpan={7} className="bg-surface-1 px-3 py-1.5 text-xs text-fg-subtle">{label} <span className="ml-1 text-fg-faint tabular-nums">{n}</span></td></tr>
  )

  return (
    <div>
      <div className="flex items-center gap-2 px-6 pt-5">
        <div className="relative w-80">
          <Search size={14} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-fg-faint" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email" className="pl-8" aria-label="Search people" />
        </div>
        <Select value={filter} onChange={(e) => setFilter(e.target.value as Filter)} aria-label="Filter by role">
          <option value="all">All</option>
          {[...ROLES].reverse().map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
        </Select>
        <div className="ml-auto"><RoleGate action="people.invite"><Button variant="primary" onClick={() => setInviting(true)}>Invite</Button></RoleGate></div>
      </div>
      <div className="space-y-4 px-6 py-4">
        {act.error && <ErrorState error={act.error} compact />}
        <Card>
          {peopleQ.error && !peopleQ.data ? <ErrorState error={peopleQ.error} retry={() => void peopleQ.refetch()} /> : !peopleQ.data ? <SkeletonRows rows={5} /> : all.length === 0 && invites.length === 0 ? (
            <EmptyState icon={<Users size={20} />} title="Nobody here yet" body="Invite someone — they set a password and enrol 2FA on first sign-in." />
          ) : (
            <Table head={['Name', 'Email', 'Role', 'Projects', 'Joined', 'Last seen', '']}>
              {section('Active', active.length)}
              {active.map(personRow)}
              {invites.length > 0 && section('Invited', invites.length)}
              {invites.map((i) => (
                <Tr key={i.id}>
                  <Td>
                    <span className="flex items-center gap-3">
                      <span className="grid size-7 shrink-0 place-items-center rounded-full border border-dashed border-border text-fg-faint"><UserRound size={14} /></span>
                      <span className="min-w-0 leading-tight"><span className="block truncate">{i.email}</span><span className="block truncate text-xs text-fg-faint">Invited by {i.invitedBy}</span></span>
                    </span>
                  </Td>
                  <Td className="text-fg-muted">{i.email}</Td>
                  <Td><RoleBadge role={i.globalRole} /></Td>
                  <Td className="text-fg-faint">—</Td>
                  <Td className="text-fg-subtle"><span title={`Expires ${dateTime(i.expiresAt)}`}>Pending</span></Td>
                  <Td className="text-fg-faint">—</Td>
                  <Td className="w-10">
                    <RoleGate action="people.invite">
                      <Menu trigger={<Button variant="ghost" size="sm" aria-label={`Actions for ${i.email}`}>⋯</Button>} items={[
                        { label: 'Resend invite', onSelect: () => act.mutate(() => post(`/api/invites/${enc(i.id)}/resend`)) },
                        'sep',
                        { label: 'Revoke invite', danger: true, onSelect: () => act.mutate(() => del(`/api/invites/${enc(i.id)}`)) },
                      ]} />
                    </RoleGate>
                  </Td>
                </Tr>
              ))}
              {inactive.length > 0 && section('Inactive', inactive.length)}
              {inactive.map(personRow)}
            </Table>
          )}
        </Card>
      </div>
      <InviteDialog open={inviting} onOpenChange={setInviting} />
      {access && <AccessDialog person={access} onClose={() => setAccess(null)} />}
      {roleFor && <RoleDialog person={roleFor} onClose={() => setRoleFor(null)} />}
    </div>
  )
}

const ROLE_LABEL: Record<Role, string> = { admin: 'Admin', lead: 'Lead', dev: 'Member', viewer: 'Viewer' }

function RoleBadge({ role, muted, label }: { role: Role; muted?: boolean; label?: string }) {
  if (label) return <span className="text-xs text-fg-faint">{label}</span>
  return <span className={cn('rounded px-1.5 py-0.5 text-xs', role === 'admin' && !muted ? 'bg-accent/15 text-accent' : 'bg-surface-3 text-fg-muted')}>{ROLE_LABEL[role]}</span>
}

function RoleDialog({ person, onClose }: { person: Person; onClose: () => void }) {
  const qc = useQueryClient()
  const [role, setRole] = useState<Role>(person.globalRole)
  const m = useMutation({
    mutationFn: () => patch(`/api/people/${enc(person.id)}`, { globalRole: role }, person.version),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: qk.people() }); onClose() },
  })
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={`Change role — ${person.name}`} description="Roles: viewer < dev < lead < admin. Admins have every right everywhere.">
      <Select value={role} onChange={(e) => setRole(e.target.value as Role)} aria-label="Role">{ROLES.map((r) => <option key={r} value={r}>{r}</option>)}</Select>
      {m.error && <ErrorState error={m.error} compact />}
      <div className="flex justify-end gap-2"><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={m.isPending || role === person.globalRole} onClick={() => m.mutate()}>Save</Button></div>
    </Dialog>
  )
}
